import { asset } from "../core/data";
import type { Preferences } from "../persistence/storage";
import type { FactionId } from "../core/types";
export class AudioManager {
  enabled = false;
  blocked = false;
  private context: AudioContext | null = null;
  private bus: GainNode | null = null;
  private cache = new Map<string, Promise<AudioBuffer | null>>();
  private sources: AudioBufferSourceNode[] = [];
  private tracks: HTMLAudioElement[] = [];
  private target = "";
  private fadeTimer = 0;
  private duckUntil = 0;
  private hidden = false;
  private current: HTMLAudioElement | null = null;
  private gains = new Map<HTMLAudioElement, number>();
  private transitionVersion = 0;
  get needsGesture() {
    return !this.enabled || this.blocked || this.context?.state === "suspended";
  }
  private visibility = () => {
    this.hidden = document.hidden;
    if (this.hidden) {
      this.tracks.forEach((t) => t.pause());
      void this.context?.suspend().catch(() => {});
    } else if (this.enabled && !this.preferences.muted) {
      void this.unlock();
    }
  };
  constructor(public preferences: Preferences) {
    document.addEventListener("visibilitychange", this.visibility);
  }
  async unlock() {
    this.enabled = true;
    this.blocked = false;
    try {
      const C =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (C && !this.context) {
        this.context = new C();
        this.bus = this.context.createGain();
        this.bus.connect(this.context.destination);
      }
      // Both calls must happen in the original gesture task. Waiting for a
      // suspended AudioContext here can prevent HTMLAudio from ever starting.
      void this.context?.resume().catch(() => {
        this.blocked = true;
      });
      this.apply();
      if (this.target) await this.transition(this.target, true);
      return true;
    } catch {
      this.blocked = true;
      return false;
    }
  }
  scene(scene: "menu" | "map" | "battle" | "finale", faction?: FactionId) {
    const key =
      scene === "map" || scene === "battle"
        ? `${faction ?? "alliance"}-${scene}`
        : scene;
    if (key === this.target) return;
    this.target = key;
    if (this.enabled)
      void this.transition(key).catch(() => {
        this.blocked = true;
      });
  }
  private async transition(key: string, restart = false) {
    if (this.hidden || (!restart && this.current?.dataset.track === key))
      return;
    const ticket = ++this.transitionVersion,
      next = new Audio();
    next.dataset.track = key;
    next.loop = true;
    next.preload = "auto";
    next.volume = 0;
    const ogg = !!next.canPlayType('audio/ogg; codecs="vorbis"');
    next.src = asset(`audio/${key}.${ogg ? "ogg" : "mp3"}`);
    this.tracks.push(next);
    this.gains.set(next, 0);
    const old = this.current;
    this.current = next;
    try {
      await next.play();
    } catch {
      if (ogg) {
        next.src = asset(`audio/${key}.mp3`);
        try {
          await next.play();
        } catch {
          this.blocked = true;
        }
      } else this.blocked = true;
    }
    if (ticket !== this.transitionVersion) {
      next.pause();
      next.removeAttribute("src");
      next.load();
      this.gains.delete(next);
      this.tracks = this.tracks.filter((t) => t !== next);
      return;
    }
    if (this.hidden) next.pause();
    window.clearInterval(this.fadeTimer);
    const start = performance.now(),
      oldGain = old ? (this.gains.get(old) ?? 1) : 0;
    this.fadeTimer = window.setInterval(() => {
      const t = Math.min(1, (performance.now() - start) / 1100);
      this.gains.set(next, t);
      if (old) this.gains.set(old, oldGain * (1 - t));
      this.apply();
      if (t === 1) {
        window.clearInterval(this.fadeTimer);
        for (const track of this.tracks.filter((t) => t !== next)) {
          track.pause();
          track.removeAttribute("src");
          track.load();
          this.gains.delete(track);
        }
        this.tracks = [next];
      }
    }, 40);
  }
  apply() {
    const p = this.preferences,
      m = p.muted ? 0 : p.master;
    if (this.bus) this.bus.gain.value = m * p.sfx;
    for (const t of this.tracks)
      t.volume = Math.max(
        0,
        Math.min(
          1,
          (this.gains.get(t) ?? 0) *
            m *
            p.music *
            (performance.now() < this.duckUntil ? 0.45 : 1),
        ),
      );
  }
  private buffer(name: string) {
    if (!this.cache.has(name))
      this.cache.set(
        name,
        (async () => {
          try {
            const r = await fetch(asset(`audio/${name}.wav`));
            if (!r.ok) return null;
            return await this.context!.decodeAudioData(await r.arrayBuffer());
          } catch {
            return null;
          }
        })(),
      );
    return this.cache.get(name)!;
  }
  async sfx(name: string) {
    if (
      !this.enabled ||
      this.hidden ||
      this.preferences.muted ||
      !this.context ||
      !this.bus
    )
      return;
    const buffer = await this.buffer(name);
    if (!buffer) return;
    if (this.sources.length >= 8) {
      const source = this.sources.shift();
      try {
        source?.stop();
      } catch {}
    }
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.bus);
    this.sources.push(source);
    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source);
      source.disconnect();
    };
    try {
      source.start();
    } catch {}
    if (name === "explosion") {
      this.duckUntil = performance.now() + 850;
      this.apply();
      setTimeout(() => this.apply(), 860);
    }
  }
  stop() {
    this.enabled = false;
    this.transitionVersion++;
    window.clearInterval(this.fadeTimer);
    this.tracks.forEach((t) => {
      t.pause();
      t.removeAttribute("src");
      t.load();
    });
    this.tracks = [];
    this.current = null;
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch {}
    });
    this.sources = [];
    this.target = "";
  }
  dispose() {
    this.stop();
    document.removeEventListener("visibilitychange", this.visibility);
    void this.context?.close().catch(() => {});
  }
}
